import React, { useState, useEffect, useRef, useMemo } from "react";
import LevelAccordion from "../components/LevelAccordion";
import CountUp from "../components/common/CountUp";
import SectionHeader from "../components/common/SectionHeader";
import { getEthersProvider } from "../web3/provider";
import { getPurchaseContract } from "../web3/contracts";
import { PACKAGES } from "../utils/constants";

export default function TeamPage({ data, goTab }) {
  const [view, setView] = useState("matrix");

  if (!data.registered) {
    return (
      <div className="card center fade-up" style={{ marginTop: 24 }}>
        <h2 className="card-title">No team yet</h2>
        <p className="muted">Activate your node to start building your team.</p>
        <button className="btn mt-4" onClick={() => goTab("home")}>Go to Home</button>
      </div>
    );
  }

  return (
    <div className="stack">
      <div className="grid grid-2">
        <div className="stat stat-accent">
          <div className="label">Direct Referrals</div>
          <div className="value grad-text"><CountUp value={data.details.totalDirectReferrals.toNumber()} decimals={0} /></div>
        </div>
        <div className="stat stat-accent">
          <div className="label">Total Team</div>
          <div className="value grad-text-cyan"><CountUp value={data.totalTeam ? data.totalTeam.toNumber() : 0} decimals={0} /></div>
        </div>
      </div>

      <DirectReferrals userId={data.userId} />

      <div className="segmented">
        <button className={view === "matrix" ? "active" : ""} onClick={() => setView("matrix")}>Matrix</button>
        <button className={view === "generation" ? "active" : ""} onClick={() => setView("generation")}>Generation</button>
      </div>

      {view === "matrix" ? (
        <MatrixLevels userId={data.userId} />
      ) : (
        <GenerationLevels userId={data.userId} />
      )}
    </div>
  );
}

function PackagePicker({ value, onChange }) {
  return (
    <select
      className="input"
      style={{ width: "auto", padding: "8px 12px", fontSize: 13 }}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
    >
      {PACKAGES.map((p) => (
        <option key={p.id} value={p.id}>#{p.id} {p.label}</option>
      ))}
    </select>
  );
}

/* ---- Direct referrals list ---- */
function DirectReferrals({ userId }) {
  const [refs, setRefs] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    (async () => {
      try {
        const c = getPurchaseContract(getEthersProvider());
        const list = await c.getUserReferrals(userId);
        if (mounted.current) setRefs(list.map((x) => x.toString()));
      } catch {
        if (mounted.current) setRefs([]);
      }
    })();
    return () => { mounted.current = false; };
  }, [userId]);

  return (
    <div className="card">
      <SectionHeader icon="users" title="Direct Referrals" accent="cyan"
        right={refs ? `${refs.length} total` : ""} />
      {refs === null && <p className="muted">Loading…</p>}
      {refs && refs.length === 0 && <p className="muted">No referrals yet. Share your link to grow your team.</p>}
      {refs && refs.length > 0 && (
        <div className="row wrap">
          {refs.map((id, i) => <span className="id-chip" key={i}>{id}</span>)}
        </div>
      )}
    </div>
  );
}

/* ---- Matrix: 10 levels per package (members stored per package) ---- */
function MatrixLevels({ userId }) {
  const [pkgId, setPkgId] = useState(1);
  const contract = useMemo(() => getPurchaseContract(getEthersProvider()), []);

  const loadLevel = async (level) => {
    const ids = await contract.getMatrixLevelUsers(userId, pkgId, level);
    return ids.map((x) => x.toString());
  };

  return (
    <div className="card">
      <SectionHeader icon="node" title="Matrix Levels" accent="violet"
        right={<PackagePicker value={pkgId} onChange={setPkgId} />} />
      <LevelAccordion count={10} loadLevel={loadLevel} resetKey={`m-${pkgId}`} />
    </div>
  );
}

/* ---- Generation: 10 levels of the sponsor tree, filtered by package ---- */
function GenerationLevels({ userId }) {
  const [pkgId, setPkgId] = useState(1);
  const contract = useMemo(() => getPurchaseContract(getEthersProvider()), []);
  const selfId = userId.toString();

  // Caches persist across package switches.
  const tree = useRef({});       // level -> string[] (sponsor tree, package-independent)
  const active = useRef({});     // memberId -> number[] of active package ids
  const lookups = useRef(0);     // safety cap on getUserPackages calls

  useEffect(() => { tree.current = {}; active.current = {}; lookups.current = 0; }, [selfId]);

  const ensureTree = async (level) => {
    for (let L = 1; L <= level; L++) {
      if (tree.current[L] !== undefined) continue;
      const parents = L === 1 ? [selfId] : tree.current[L - 1] || [];
      if (parents.length === 0) { tree.current[L] = []; continue; }
      const capped = parents.slice(0, 300);
      const lists = await Promise.all(capped.map((pid) => contract.getUserReferrals(pid).catch(() => [])));
      const ids = [];
      lists.forEach((arr) => arr.forEach((x) => ids.push(x.toString())));
      tree.current[L] = ids;
    }
    return tree.current[level];
  };

  const isActive = async (id) => {
    if (active.current[id] === undefined) {
      if (lookups.current > 800) return true; // safety: include rather than hammer RPC
      lookups.current += 1;
      try {
        const flags = await contract.getUserPackages(id); // bool[9]
        active.current[id] = flags.map((b, i) => (b ? i + 1 : 0)).filter(Boolean);
      } catch {
        active.current[id] = [];
      }
    }
    return active.current[id].includes(pkgId);
  };

  const loadLevel = async (level) => {
    const members = await ensureTree(level);
    const out = [];
    for (const id of members) {
      // eslint-disable-next-line no-await-in-loop
      if (await isActive(id)) out.push(id);
    }
    return out;
  };

  return (
    <div className="card">
      <SectionHeader icon="layers" title="Generation Levels" accent="violet"
        right={<PackagePicker value={pkgId} onChange={setPkgId} />} />
      <p className="muted" style={{ fontSize: 12, marginTop: -8, marginBottom: 12 }}>
        Sponsor-tree members active in the selected package.
      </p>
      <LevelAccordion count={10} loadLevel={loadLevel} resetKey={`g-${selfId}-${pkgId}`} />
    </div>
  );
}
